import { useEffect, useState } from "react";
import {
  Col,
  Row,
  Typography,
  Card,
  Space,
  Statistic,
  Alert,
  Skeleton,
  Progress,
  Tag,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faShieldHalved,
  faGraduationCap,
  faHandshakeAngle,
  faClipboardCheck,
  faBrain,
  faUsers,
  faUserGroup,
  faCalendarDay,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import {
  getCompanyUsers,
  getCompanyEmployees,
  getCompanyPolicies,
  getCompanyDepartments,
  getCompanyPolicyAcknowledgments,
  getTrainings,
  getWellbeingReports,
} from "../repository/data.repository";
import { MAX_PAGE_SIZE } from "../repository/data.default";

// PD-02 §6.7 Analytics — six reporting areas. Numbers come from live
// endpoints; aggregation rules + privacy thresholds are documented in §11
// and respected server-side.
function CompanyAnalyticsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    users: 0,
    employees: 0,
    policies: 0,
    departments: 0,
    acknowledgments: 0,
    trainingsTotal: 0,
    trainingsCompleted: 0,
    trainingsCancelled: 0,
    wellbeingReports: 0,
  });

  useEffect(() => {
    const q = { searchQuery: { conditions: [] }, pageNumber: 0, pageSize: 1 };
    const qLarge = {
      searchQuery: { conditions: [] },
      pageNumber: 0,
      pageSize: MAX_PAGE_SIZE,
    };
    Promise.all([
      getCompanyUsers(q).catch(() => null),
      getCompanyEmployees(q).catch(() => null),
      getCompanyPolicies(q).catch(() => null),
      getCompanyDepartments(q).catch(() => null),
      getCompanyPolicyAcknowledgments(q).catch(() => null),
      getTrainings(qLarge).catch(() => null),
      getWellbeingReports(q).catch(() => null),
    ])
      .then(([u, e, p, d, a, t, w]) => {
        const trainings = (t?.data ?? []) as any[];
        setStats({
          users: u?.totalRecords ?? 0,
          employees: e?.totalRecords ?? 0,
          policies: p?.totalRecords ?? 0,
          departments: d?.totalRecords ?? 0,
          acknowledgments: a?.totalRecords ?? 0,
          trainingsTotal: trainings.length,
          trainingsCompleted: trainings.filter((x: any) => x.status === "Completed").length,
          trainingsCancelled: trainings.filter((x: any) => x.status === "Cancelled").length,
          wellbeingReports: w?.totalRecords ?? 0,
        });
      })
      .finally(() => setLoading(false));
  }, []);

  // Policy acknowledgment rate = acknowledgments / (employees × policies).
  // A rough proxy; the backend's own report will do the proper per-policy roll-up.
  const expectedAcks = stats.employees * stats.policies;
  const ackRate =
    expectedAcks > 0 ? Math.min(100, (stats.acknowledgments / expectedAcks) * 100) : 0;

  const trainingCompletionRate =
    stats.trainingsTotal > 0
      ? Math.round((stats.trainingsCompleted / stats.trainingsTotal) * 100)
      : 0;

  return (
    <Col className="flex flex-auto flex-col gap-4 overflow-y-auto p-4">
      <Row>
        <Typography.Title level={3} className="!m-0">
          <FontAwesomeIcon icon={faChartLine} className="mr-2" />
          Analytics
        </Typography.Title>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <Card bodyStyle={{ padding: 16 }}>
            {loading ? (
              <Skeleton paragraph={{ rows: 1 }} active />
            ) : (
              <Statistic
                title="Company users"
                value={stats.users}
                prefix={<FontAwesomeIcon icon={faUsers} />}
              />
            )}
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card bodyStyle={{ padding: 16 }}>
            {loading ? (
              <Skeleton paragraph={{ rows: 1 }} active />
            ) : (
              <Statistic
                title="Employees"
                value={stats.employees}
                prefix={<FontAwesomeIcon icon={faUserGroup} />}
              />
            )}
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card bodyStyle={{ padding: 16 }}>
            {loading ? (
              <Skeleton paragraph={{ rows: 1 }} active />
            ) : (
              <Statistic
                title="Policies"
                value={stats.policies}
                prefix={<FontAwesomeIcon icon={faShieldHalved} />}
              />
            )}
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card bodyStyle={{ padding: 16 }}>
            {loading ? (
              <Skeleton paragraph={{ rows: 1 }} active />
            ) : (
              <Statistic
                title="Departments"
                value={stats.departments}
                prefix={<FontAwesomeIcon icon={faCalendarDay} />}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Alert
        type="info"
        showIcon
        message="Aggregated views respect the configured privacy and completion thresholds — numbers below reflect what is currently visible to this company user."
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card
            hoverable
            onClick={() => navigate("../metrics")}
            bodyStyle={{ padding: 16 }}
          >
            <Space direction="vertical" className="w-full">
              <FontAwesomeIcon icon={faHandshakeAngle} className="text-2xl" />
              <Typography.Title level={5} className="!m-0">
                Workforce and Engagement
              </Typography.Title>
              <Statistic
                title="Registered users"
                value={stats.users}
                suffix={`/ ${stats.employees} employees`}
              />
              <Typography.Text type="secondary" className="text-xs">
                Click to open organizational metrics.
              </Typography.Text>
            </Space>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card bodyStyle={{ padding: 16 }}>
            <Space direction="vertical" className="w-full">
              <FontAwesomeIcon icon={faBrain} className="text-2xl" />
              <Typography.Title level={5} className="!m-0">
                Psychosocial Risk & Wellbeing
              </Typography.Title>
              {stats.wellbeingReports > 0 ? (
                <>
                  <Statistic
                    title="Published reports"
                    value={stats.wellbeingReports}
                  />
                  <Typography.Text type="secondary" className="text-xs">
                    Aggregated risk reports published back to this company by
                    the platform operator. Open Wellbeing Reports to drill in.
                  </Typography.Text>
                  <Tag color="success">Reports available</Tag>
                </>
              ) : (
                <>
                  <Typography.Text type="secondary" className="text-xs">
                    Overall risk distribution, assessment results and pillar
                    summary — published by the platform operator once the
                    configured completion + privacy threshold is met.
                  </Typography.Text>
                  <Tag color="processing">Awaiting threshold</Tag>
                </>
              )}
            </Space>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card
            hoverable
            onClick={() => navigate("../processes/overview")}
            bodyStyle={{ padding: 16 }}
          >
            <Space direction="vertical" className="w-full">
              <FontAwesomeIcon icon={faClipboardCheck} className="text-2xl" />
              <Typography.Title level={5} className="!m-0">
                Process & Action Follow-Up
              </Typography.Title>
              <Typography.Text type="secondary" className="text-xs">
                Roadmap stage progress, open and overdue actions and
                completion rate. Jump into the roadmap overview to drill in.
              </Typography.Text>
              <Tag color="blue">Open Processes</Tag>
            </Space>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card
            hoverable
            onClick={() => navigate("../policies")}
            bodyStyle={{ padding: 16 }}
          >
            <Space direction="vertical" className="w-full">
              <FontAwesomeIcon icon={faShieldHalved} className="text-2xl" />
              <Typography.Title level={5} className="!m-0">
                Policy Follow-Up
              </Typography.Title>
              <Statistic
                title="Acknowledgment rate (approx)"
                value={Math.round(ackRate)}
                suffix="%"
              />
              <Progress percent={Math.round(ackRate)} showInfo={false} />
              <Typography.Text type="secondary" className="text-xs">
                {stats.acknowledgments} of ~{expectedAcks} expected
                acknowledgments. Open Policies to see per-policy details.
              </Typography.Text>
            </Space>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card bodyStyle={{ padding: 16 }}>
            <Space direction="vertical" className="w-full">
              <FontAwesomeIcon icon={faGraduationCap} className="text-2xl" />
              <Typography.Title level={5} className="!m-0">
                Training & Intervention Follow-Up
              </Typography.Title>
              <Row gutter={8}>
                <Col flex="1 1 100px">
                  <Statistic
                    title="Total"
                    value={stats.trainingsTotal}
                  />
                </Col>
                <Col flex="1 1 100px">
                  <Statistic
                    title="Completed"
                    value={stats.trainingsCompleted}
                  />
                </Col>
                <Col flex="1 1 100px">
                  <Statistic
                    title="Cancelled"
                    value={stats.trainingsCancelled}
                  />
                </Col>
              </Row>
              <Progress percent={trainingCompletionRate} showInfo />
              <Typography.Text type="secondary" className="text-xs">
                Completion rate across all planned trainings in scope.
              </Typography.Text>
            </Space>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card bodyStyle={{ padding: 16 }}>
            <Space direction="vertical" className="w-full">
              <FontAwesomeIcon icon={faHandshakeAngle} className="text-2xl" />
              <Typography.Title level={5} className="!m-0">
                Support & Request Visibility
              </Typography.Title>
              <Typography.Text type="secondary" className="text-xs">
                Operational information only — open support workflows and
                permitted request status. No confidential clinical content
                is surfaced on this dashboard (PD-02 §11).
              </Typography.Text>
              <Tag color="purple">Operational-only</Tag>
            </Space>
          </Card>
        </Col>
      </Row>
    </Col>
  );
}

export default CompanyAnalyticsPage;
